import React, { useState, useEffect, useCallback, useMemo, useId } from 'react';
import { toast } from 'react-toastify';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { listSDKApiKeys, createSDKApiKey, revokeSDKApiKey, getSDKApiLogs, getSDKApiStats } from '../../services/api';
import { useModal } from '../../contexts/ModalContext';

const SDKPage = () => {
  const { currentWorkspace } = useWorkspace();
  const { confirmDelete } = useModal();
  const gradientId = useId();
  const [activeTab, setActiveTab] = useState('keys');
  const [apiKeys, setApiKeys] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null);
  const [logs, setLogs] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState(['read']);
  const [createdKey, setCreatedKey] = useState(null);
  const [creating, setCreating] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const workspaceId = currentWorkspace?.id || currentWorkspace?._id;
  
  // Check user permissions
  const isOwner = currentWorkspace?.user_role === 'owner';
  const isAdmin = currentWorkspace?.user_role === 'admin' || isOwner;
  const canManageApiKeys = isAdmin;

  const chartData = useMemo(() => {
    if (!allLogs || allLogs.length === 0) return [];
    
    return allLogs
      .filter(log => log.latency_ms && log.created_at)
      .map(log => ({
        timestamp: new Date(log.created_at).getTime(),
        latency: log.latency_ms,
        endpoint: log.endpoint,
        status: log.status_code
      }))
      .sort((a, b) => a.timestamp - b.timestamp)
      .slice(-50);
  }, [allLogs]);

  const latencyStats = useMemo(() => {
    if (chartData.length === 0) return null;
    const latencies = chartData.map(d => d.latency);
    return {
      avg: latencies.reduce((a, b) => a + b, 0) / latencies.length,
      min: Math.min(...latencies),
      max: Math.max(...latencies)
    };
  }, [chartData]);

  const loadApiKeys = useCallback(async () => {
    if (!workspaceId) return;
    
    setLoading(true);
    const result = await listSDKApiKeys(workspaceId);
    if (result.success) {
      setApiKeys(result.data.keys || []);
      if (result.data.keys?.length > 0 && !selectedKey) {
        setSelectedKey(result.data.keys[0]);
      }
    } else {
      toast.error(result.error || 'Failed to load API keys');
    }
    setLoading(false);
  }, [workspaceId, selectedKey]);

  const loadLogs = useCallback(async (keyId = null) => {
    if (!workspaceId) return;
    
    setLogsLoading(true);
    const result = await getSDKApiLogs(workspaceId, { 
      keyId: keyId || selectedKey?.key_id,
      limit: 100 
    });
    if (result.success) {
      setLogs(result.data.logs || []);
    } else {
      toast.error(result.error || 'Failed to load logs');
    }
    setLogsLoading(false);
  }, [workspaceId, selectedKey]);

  const loadStats = useCallback(async () => {
    if (!workspaceId) return;
    
    const result = await getSDKApiStats(workspaceId, 30);
    if (result.success) {
      setStats(result.data);
    }
  }, [workspaceId]);

  const loadAllLogs = useCallback(async () => {
    if (!workspaceId) return;
    
    const result = await getSDKApiLogs(workspaceId, { limit: 100 });
    if (result.success) {
      setAllLogs(result.data.logs || []);
    }
  }, [workspaceId]);

  const refreshLogsData = useCallback(async () => {
    await Promise.all([
      loadStats(),
      loadAllLogs()
    ]);
  }, [loadStats, loadAllLogs]);

  useEffect(() => {
    loadApiKeys();
  }, [loadApiKeys]);

  useEffect(() => {
    refreshLogsData();
  }, [refreshLogsData]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      refreshLogsData();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshLogsData]);

  const handleRefresh = () => {
    setLogsLoading(true);
    refreshLogsData();
  };

  useEffect(() => {
    if (activeTab === 'logs' && selectedKey) {
      loadLogs(selectedKey.key_id);
    }
  }, [activeTab, selectedKey, loadLogs]);

  const handleCreateKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a name for the API key');
      return;
    }

    setCreating(true);
    const result = await createSDKApiKey(workspaceId, newKeyName.trim(), newKeyPermissions);
    if (result.success) {
      setCreatedKey(result.data);
      toast.success('API key created successfully');
      loadApiKeys();
      setNewKeyName('');
      setNewKeyPermissions(['read']);
    } else {
      toast.error(result.error || 'Failed to create API key');
    }
    setCreating(false);
  };

  const handleRevokeKey = async (key) => {
    const confirmed = await confirmDelete(`API Key "${key.name}"`);
    if (!confirmed) return;

    const result = await revokeSDKApiKey(workspaceId, key.key_id);
    if (result.success) {
      toast.success('API key revoked');
      if (selectedKey?.key_id === key.key_id) {
        setSelectedKey(null);
      }
      loadApiKeys();
    } else {
      toast.error(result.error || 'Failed to revoke API key');
    }
  };

  const handleSelectKey = (key) => {
    setSelectedKey(key);
    if (activeTab === 'logs') {
      loadLogs(key.key_id);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const togglePermission = (perm) => {
    setNewKeyPermissions(prev => 
      prev.includes(perm) 
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };

  if (!currentWorkspace) {
    return (
      <div className="flex-1 flex items-center justify-center p-10">
        <div className="text-center">
          <p className="text-text-tertiary">Please select a workspace first</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden bg-bg-primary">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div>
            <h1 className="page-title">API Access</h1>
            <p className="page-subtitle">
              Manage API keys and SDK integration for your workspace
            </p>
          </div>
        </div>
        
        <div className="page-actions">
          {/* Auto-refresh toggle */}
          {activeTab === 'logs' && (
            <label className="flex items-center gap-2 text-sm px-3 py-2 rounded-lg bg-bg-tertiary border border-border-primary hover:bg-bg-hover transition-colors cursor-pointer select-none">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                className="rounded border-border-primary focus:ring-offset-0 bg-bg-input cursor-pointer accent-[#32CD32]"
              />
              <span className="text-text-secondary text-xs sm:text-sm whitespace-nowrap">Auto-refresh</span>
            </label>
          )}
          
          {/* Manual refresh button */}
          {activeTab === 'logs' && (
            <button
              onClick={handleRefresh}
              disabled={logsLoading}
              className="btn-secondary"
              title="Refresh data"
            >
              <svg
                className={`h-4 w-4 ${logsLoading ? 'animate-spin' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              <span className="hidden sm:inline">Refresh</span>
            </button>
          )}

          {/* Create API Key button - only visible for owners and admins */}
          {canManageApiKeys && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-2 bg-gradient-to-br from-primary-600 to-primary-500 text-black shadow-glow hover:shadow-glow-lg"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Create API Key
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-border-primary bg-bg-primary px-4 sm:px-6">
        <nav className="-mb-px flex gap-2 overflow-x-auto scrollbar-hide" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('keys')}
            className={`
              group inline-flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200
              ${activeTab === 'keys'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-primary'
              }
            `}
          >
            <span className={`transition-transform duration-200 ${activeTab === 'keys' ? 'scale-110' : 'group-hover:scale-105'}`}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
              </svg>
            </span>
            <span>API Keys</span>
            {activeTab === 'keys' && (
              <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-primary-500"></span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`
              group inline-flex items-center gap-2 px-4 py-3 border-b-2 font-medium text-sm whitespace-nowrap transition-all duration-200
              ${activeTab === 'logs'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-text-tertiary hover:text-text-primary hover:border-border-primary'
              }
            `}
          >
            <span className={`transition-transform duration-200 ${activeTab === 'logs' ? 'scale-110' : 'group-hover:scale-105'}`}>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25z" />
              </svg>
            </span>
            <span>SDK Logs</span>
            {activeTab === 'logs' && (
              <span className="inline-flex items-center justify-center w-1.5 h-1.5 rounded-full bg-primary-500"></span>
            )}
          </button>
        </nav>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden flex">
        {activeTab === 'keys' ? (
          /* API Keys Tab */
          <div className="flex-1 overflow-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center h-40">
                <div className="w-8 h-8 border-2 border-primary-500/20 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : apiKeys.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center bg-success-50 border border-primary-500/20">
                  <svg className="w-8 h-8 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                  </svg>
                </div>
                <div className="text-center">
                  <h3 className="text-lg font-medium text-text-primary">No API Keys</h3>
                  <p className="text-sm mt-1 text-text-tertiary">
                    {canManageApiKeys 
                      ? 'Create an API key to access your prompts via the SDK'
                      : 'Contact a workspace owner or admin to create API keys'}
                  </p>
                </div>
                {canManageApiKeys && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 text-sm font-medium rounded-lg transition-all bg-success-50 text-primary-500 border border-primary-500/30 hover:bg-success-100"
                  >
                    Create Your First API Key
                  </button>
                )}
              </div>
            ) : (
              <div className="grid gap-4">
                {apiKeys.map((key) => (
                  <div
                    key={key.key_id}
                    className={`p-4 rounded-xl border transition-all duration-200 cursor-pointer hover:border-primary-500/30 ${
                      selectedKey?.key_id === key.key_id 
                        ? 'ring-2 ring-primary-500/30 bg-primary-500/5 border-primary-500/40' 
                        : 'bg-surface-primary/50 border-border-secondary'
                    }`}
                    onClick={() => handleSelectKey(key)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-base font-medium text-text-primary">{key.name}</h3>
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full ${
                              key.is_active 
                                ? 'bg-success-50 text-primary-500' 
                                : 'bg-error-50 text-error'
                            }`}
                          >
                            {key.is_active ? 'Active' : 'Revoked'}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-text-tertiary">
                            ID: <span className="text-text-secondary">{key.key_id}</span>
                          </span>
                          <span className="text-xs text-text-tertiary">
                            Created: <span className="text-text-secondary">{formatDate(key.created_at)}</span>
                          </span>
                          <span className="text-xs text-text-tertiary">
                            Last used: <span className="text-text-secondary">{formatDate(key.last_used_at)}</span>
                          </span>
                        </div>
                        <div className="flex items-center gap-2 mt-3">
                          {key.permissions?.map((perm) => (
                            <span
                              key={perm}
                              className="px-2 py-0.5 text-xs rounded bg-success-50 text-primary-500"
                            >
                              {perm}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); copyToClipboard(key.key_id); }}
                          className="p-2 rounded-lg transition-all bg-success-50 hover:bg-success-100 text-primary-500"
                          title="Copy Key ID"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        {key.is_active && canManageApiKeys && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRevokeKey(key); }}
                            className="p-2 rounded-lg transition-all bg-error-50 hover:bg-error-100 text-error"
                            title="Revoke Key"
                          >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          /* SDK Logs Tab */
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Latency Chart */}
            <div className="flex-shrink-0 mx-6 mt-4 rounded-lg p-4 bg-surface-primary/60 border border-border-secondary">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-4">
                  <h3 className="text-sm font-semibold text-text-primary">
                    SDK Latency
                  </h3>
                  {latencyStats && (
                    <div className="flex items-center gap-4">
                      <span className="text-xs text-text-tertiary">
                        Avg: <span className="text-primary-500">{latencyStats.avg.toFixed(0)}ms</span>
                      </span>
                      <span className="text-xs text-text-tertiary">
                        Min: <span className="text-text-secondary">{latencyStats.min.toFixed(0)}ms</span>
                      </span>
                      <span className="text-xs text-text-tertiary">
                        Max: <span className="text-text-secondary">{latencyStats.max.toFixed(0)}ms</span>
                      </span>
                    </div>
                  )}
                </div>
                {stats && (
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-text-tertiary">
                      Requests: <span className="text-primary-500">{stats.total_requests || 0}</span>
                    </span>
                    <span className="text-xs text-text-tertiary">
                      Success: <span className="text-primary-500">
                        {stats.total_requests ? ((stats.successful_requests / stats.total_requests) * 100).toFixed(1) : 0}%
                      </span>
                    </span>
                  </div>
                )}
              </div>
              
              {chartData.length > 0 ? (
                <div className="h-[140px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={chartData}
                      margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id={`sdkLatencyGradient-${gradientId}`} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#32CD32" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#32CD32" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        stroke="rgba(50, 205, 50, 0.1)" 
                        vertical={false}
                      />
                      <XAxis 
                        dataKey="timestamp"
                        tickFormatter={(ts) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        stroke="#6B8E6B"
                        fontSize={10}
                        tickLine={false}
                        axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
                        tick={{ fill: '#6B8E6B' }}
                      />
                      <YAxis 
                        tickFormatter={(v) => `${v}ms`}
                        stroke="#6B8E6B"
                        fontSize={10}
                        tickLine={false}
                        axisLine={{ stroke: 'rgba(50, 205, 50, 0.2)' }}
                        tick={{ fill: '#6B8E6B' }}
                        width={50}
                      />
                      <Tooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const data = payload[0].payload;
                            return (
                              <div className="rounded-lg p-2 shadow-lg bg-bg-secondary/95 border border-primary-500/30">
                                <p className="text-xs text-text-tertiary">
                                  {new Date(data.timestamp).toLocaleString()}
                                </p>
                                <p className="text-sm font-semibold text-primary-500">
                                  {data.latency.toFixed(0)}ms
                                </p>
                                <p className="text-xs text-text-secondary">
                                  {data.endpoint}
                                </p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="latency"
                        stroke="#32CD32"
                        strokeWidth={2}
                        fill={`url(#sdkLatencyGradient-${gradientId})`}
                        dot={false}
                        activeDot={{ 
                          r: 4, 
                          fill: '#32CD32', 
                          stroke: '#111', 
                          strokeWidth: 2 
                        }}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[140px] flex items-center justify-center">
                  <p className="text-sm text-text-tertiary">No latency data available</p>
                </div>
              )}
            </div>

            {/* Key Selector */}
            <div className="flex-shrink-0 px-6 py-3 border-b border-border-secondary">
              <div className="flex items-center gap-3">
                <span className="text-sm text-text-tertiary">Filter by API Key:</span>
                <select
                  value={selectedKey?.key_id || ''}
                  onChange={(e) => {
                    const key = apiKeys.find(k => k.key_id === e.target.value);
                    setSelectedKey(key || null);
                  }}
                  className="px-3 py-1.5 text-sm rounded-lg bg-surface-primary/80 border border-primary-500/20 text-text-primary outline-none focus:border-primary-500/40"
                >
                  <option value="">All Keys</option>
                  {apiKeys.map((key) => (
                    <option key={key.key_id} value={key.key_id}>{key.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => loadLogs(selectedKey?.key_id)}
                  className="p-1.5 rounded-lg transition-all bg-success-50 hover:bg-success-100 text-primary-500"
                  title="Refresh logs"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Logs Table */}
            <div className="flex-1 overflow-auto p-6">
              {logsLoading ? (
                <div className="flex items-center justify-center h-40">
                  <div className="w-8 h-8 border-2 border-primary-500/20 border-t-primary-500 rounded-full animate-spin" />
                </div>
              ) : logs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 gap-4">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center bg-success-50 border border-primary-500/20">
                    <svg className="w-8 h-8 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
                    </svg>
                  </div>
                  <div className="text-center">
                    <h3 className="text-lg font-medium text-text-primary">No SDK Logs</h3>
                    <p className="text-sm mt-1 text-text-tertiary">
                      API requests will appear here once you start using the SDK
                    </p>
                  </div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border-secondary">
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-tertiary">Date</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-tertiary">Time</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-tertiary">Endpoint</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-tertiary">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-text-tertiary">Latency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr 
                          key={log.id} 
                          className="transition-colors border-b border-primary-500/5 hover:bg-primary-500/[0.03]"
                        >
                          <td className="py-3 px-4 text-xs text-text-secondary">
                            {log.created_at ? new Date(log.created_at).toLocaleDateString() : '-'}
                          </td>
                          <td className="py-3 px-4 text-xs text-text-secondary">
                            {log.created_at ? new Date(log.created_at).toLocaleTimeString() : '-'}
                          </td>
                          <td className="py-3 px-4 text-xs font-mono text-text-primary">
                            <span className="mr-2 text-text-tertiary">{log.method}</span>
                            {log.endpoint}
                          </td>
                          <td className="py-3 px-4">
                            <span
                              className={`px-2 py-0.5 text-xs rounded-full ${
                                log.status_code < 400 
                                  ? 'bg-success-50 text-primary-500' 
                                  : 'bg-error-50 text-error'
                              }`}
                            >
                              {log.status_code}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-xs text-text-secondary">
                            {log.latency_ms?.toFixed(0)}ms
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-bg-primary/80">
          <div className="w-full max-w-md rounded-xl p-6 bg-gradient-to-br from-bg-secondary to-surface-primary border border-primary-500/20 shadow-2xl">
            {createdKey ? (
              /* Show Created Key */
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-success-50">
                    <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-text-primary">API Key Created!</h2>
                    <p className="text-xs text-text-tertiary">Store this key securely - it won't be shown again</p>
                  </div>
                </div>
                
                <div className="mt-4 p-3 rounded-lg bg-primary-500/5 border border-primary-500/20">
                  <div className="flex items-center justify-between gap-2">
                    <code className="text-sm break-all text-primary-500">{createdKey.api_key}</code>
                    <button
                      onClick={() => copyToClipboard(createdKey.api_key)}
                      className="flex-shrink-0 p-2 rounded-lg transition-all bg-success-50 hover:bg-success-100 text-primary-500"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <button
                  onClick={() => { setShowCreateModal(false); setCreatedKey(null); }}
                  className="w-full mt-6 py-2.5 text-sm font-medium rounded-lg transition-all bg-gradient-to-br from-primary-600 to-primary-500 text-black hover:shadow-glow"
                >
                  Done
                </button>
              </div>
            ) : (
              /* Create Key Form */
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-text-primary">Create API Key</h2>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="p-1 rounded-lg transition-all text-text-tertiary hover:text-text-secondary"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-secondary">Name</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g., Production API Key"
                      className="w-full px-4 py-2.5 text-sm rounded-lg bg-surface-primary/80 border border-primary-500/20 text-text-primary outline-none focus:border-primary-500/40 placeholder:text-text-muted"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-secondary">Permissions</label>
                    <div className="flex flex-wrap gap-2">
                      {['read', 'execute'].map((perm) => (
                        <button
                          key={perm}
                          onClick={() => togglePermission(perm)}
                          className={`px-3 py-1.5 text-sm rounded-lg transition-all border ${
                            newKeyPermissions.includes(perm)
                              ? 'bg-success-100 border-primary-500/50 text-primary-500'
                              : 'bg-primary-500/5 border-border-secondary text-text-tertiary hover:border-primary-500/30'
                          }`}
                        >
                          {perm}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs mt-2 text-text-tertiary">
                      <span className="font-medium">read:</span> List and fetch prompts • 
                      <span className="font-medium"> execute:</span> Run prompts
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="flex-1 py-2.5 text-sm font-medium rounded-lg transition-all bg-transparent border border-primary-500/20 text-text-secondary hover:border-primary-500/40"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateKey}
                    disabled={creating || !newKeyName.trim()}
                    className="flex-1 py-2.5 text-sm font-medium rounded-lg transition-all disabled:opacity-50 bg-gradient-to-br from-primary-600 to-primary-500 text-black hover:shadow-glow"
                  >
                    {creating ? 'Creating...' : 'Create Key'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

SDKPage.propTypes = {};

export default SDKPage;
